<template>
  <div
    v-if="hasProjectContext"
    class="overview-decision-banner mb-6"
    data-testid="overview-decision-alert"
  >
    <div class="flex items-center gap-2 mb-3">
      <i class="pi pi-exclamation-triangle overview-decision-banner__icon" aria-hidden="true" />
      <h3 class="overview-decision-banner__heading m-0">
        {{ $t('UserProjects.overview.decision.title') }}
      </h3>
    </div>

    <div class="flex flex-col md:flex-row md:items-start justify-between gap-4">
      <div class="flex-1 min-w-0">
        <div
          v-if="!nextDecision"
          class="text-sm text-[var(--color-disabled)]"
          data-testid="overview-decision-empty"
        >
          {{ $t('UserProjects.overview.decision.empty') }}
        </div>

        <template v-else>
          <div class="flex items-center gap-2 mb-2 flex-wrap">
            <p class="overview-decision-banner__title m-0">{{ nextDecision.title }}</p>
            <span
              class="overview-decision-banner__status-badge"
              :class="nextDecision.isOverdue
                ? 'overview-decision-banner__status-badge--overdue'
                : 'overview-decision-banner__status-badge--open'"
            >
              {{ nextDecision.isOverdue
                ? $t('UserProjects.overview.decision.overdue')
                : $t('UserProjects.overview.decision.open') }}
            </span>
          </div>

          <p v-if="descriptionText" class="overview-decision-banner__impact m-0">
            {{ descriptionText }}
          </p>

          <div class="flex flex-wrap gap-x-5 gap-y-1 overview-decision-banner__meta">
            <span>
              <span class="overview-decision-banner__meta-label">
                {{ $t('UserProjects.overview.decision.decisionOwner') }}:
              </span>
              <span class="font-medium text-[var(--color-dark)]">{{ ownerLabel }}</span>
            </span>
            <span>
              <span class="overview-decision-banner__meta-label">
                {{ $t('UserProjects.overview.decision.dueDate') }}:
              </span>
              <span
                class="font-medium"
                :class="nextDecision.isOverdue ? 'text-[var(--color-error-text)]' : 'text-[var(--color-dark)]'"
              >
                {{ formatDate(nextDecision.dueDate) }}
              </span>
            </span>
            <span v-if="milestoneLabel">
              <span class="overview-decision-banner__meta-label">
                {{ $t('UserProjects.overview.decision.milestone') }}:
              </span>
              <span class="font-medium text-[var(--color-dark)]">{{ milestoneLabel }}</span>
            </span>
          </div>
        </template>
      </div>

      <button
        type="button"
        class="overview-decision-banner__cta shrink-0 self-start"
        data-testid="overview-decision-view-log"
        @click="$emit('open-decision-log')"
      >
        {{ $t('UserProjects.overview.decision.viewLog') }}
        <i class="pi pi-chevron-right text-xs" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'OverviewDecisionAlert',
  emits: ['open-decision-log'],
  inject: {
    participantUsersProvider: { from: 'participantUsers', default: () => () => [] },
  },
  computed: {
    ...mapGetters('pmCommitment', {
      overview: 'overview',
    }),
    ...mapGetters('pmProject', {
      hasProjectContext: 'hasUserProjectContext',
    }),
    nextDecision() {
      return this.overview?.nextRequiredDecision ?? null;
    },
    participantUsers() {
      return this.participantUsersProvider() || [];
    },
    ownerLabel() {
      if (this.nextDecision?.decisionOwnerDisplayName) {
        return this.nextDecision.decisionOwnerDisplayName;
      }
      if (!this.nextDecision?.decisionOwnerUserId) return '—';
      const user = this.participantUsers.find(
        (item) => String(item.id) === String(this.nextDecision.decisionOwnerUserId),
      );
      if (!user) return this.nextDecision.decisionOwnerUserId;
      return user.name || user.email || this.nextDecision.decisionOwnerUserId;
    },
    descriptionText() {
      if (!this.nextDecision) return '';
      return this.nextDecision.impactIfDelayed || this.nextDecision.basis || '';
    },
    milestoneLabel() {
      if (this.nextDecision?.milestoneTitle) {
        return this.nextDecision.milestoneTitle;
      }
      if (!this.nextDecision?.milestoneId) return '';
      const fromOverview = (this.overview?.milestones || []).find(
        (item) => String(item.id) === String(this.nextDecision.milestoneId),
      );
      return fromOverview?.title || this.nextDecision.milestoneId;
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
.overview-decision-banner {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-left: 4px solid var(--color-orange, #e4821a);
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  min-width: 0;
}

.overview-decision-banner__icon {
  color: var(--color-orange, #e4821a);
  font-size: 1rem;
  flex-shrink: 0;
}

.overview-decision-banner__heading {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #ababab;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
  min-width: 0;
}

.overview-decision-banner__title {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-dark, #1e2019);
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}

.overview-decision-banner__status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.overview-decision-banner__status-badge--overdue {
  background: #fef2f2;
  color: #ef4444;
}

.overview-decision-banner__status-badge--open {
  background: #fefce8;
  color: #eab308;
}

.overview-decision-banner__impact {
  font-size: 13px;
  color: var(--color-disabled, #6b6b6b);
  margin-bottom: 0.75rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.overview-decision-banner__meta {
  font-size: 12px;
}

.overview-decision-banner__meta-label {
  color: #ababab;
}

.overview-decision-banner__meta > span {
  overflow-wrap: anywhere;
  min-width: 0;
}

.overview-decision-banner__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
  color: var(--color-dark, #1e2019);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;
  white-space: normal;
  text-align: center;
  line-height: 1.3;
  max-width: 100%;
}

.overview-decision-banner__cta:hover {
  background: #f5f5f5;
}
</style>
